import { FC, Fragment, useEffect, useMemo, useRef } from 'react';
import {
  CalcMethod,
  EventKey,
  InstrumentType,
  OwnerType,
} from '@app/shared/models/contracts/enums/shared-enums';
import { currencyDenominationPair } from '@app/shared/helpers';
import CellValue from '@app/shared/components/cell-value/CellValue';
import {
  amountRules,
  decimalCalculatedViewRules,
  enteredPercentageViewRules,
} from '@app/shared/components/cell-value/CellValueConfigurations';
import styles from './cap-table.module.scss';
import { Alignment } from '@app/shared/enums/alignment.enum';
import { getEquityInstrumentCountByType } from '@app/shared/helpers/equity-instruments/get-equity-instrument-count-by-type';
import { getCarryingValueClarificationKey } from '@app/shared/helpers/equity-instruments/get-carrying-value-clarification-key';
import classNames from 'classnames';
import { useLocale } from '@app/core/hooks/useLocale';
import { generatePath, Navigate, useParams } from 'react-router-dom';
import { dateFormatter, getCalculatedProjectValueInPercent } from '@app/shared/formatters';
import { FormProvider, useForm } from 'react-hook-form';
import { formConfigBase } from '@app/shared/constants/form-config-base';
import { FormSelect } from '@app/shared/components/form-controls/form-select/FormSelect';
import { RouteConstants } from '@app/modules/projects/RouteConstants';
import {
  setCapitalStructureSelectedTab,
  setCapTableErfSelectedEventIndex,
} from '@app/core/store/ui-values-slice';
import useSetSelectedTab from '@app/core/hooks/useSetSelectedTab';
import { selectCapitalStructures } from '@app/core/store/capital-structure-slice-selectors';
import useInstrumentSort from '@app/shared/hooks/useInstrumentSort';
import { useStickyOffset } from '@app/core/hooks/useStickyOffset';
import { useAppDispatch, useAppSelector } from '@app/core/hooks/redux-hooks';
import { getCountWithDenomination, getDisambiguationLabel } from '@app/shared/utils/LabelHelper';

const PARENT_CLASSNAME = 'cap-table';

const CapTableErf: FC = () => {
  useSetSelectedTab(RouteConstants.CapTable, setCapitalStructureSelectedTab);
  const { caseId } = useParams();
  const { l } = useLocale();
  const dispatch = useAppDispatch();
  const eventDateDdRef = useRef<HTMLDivElement>(null);
  const { setSecondaryStickyElementHeight } = useStickyOffset();

  const { capTable: tableResults, buildStructures: buildStructureResults } = useAppSelector(
    (state) => state.capitalStructure.values
  );
  const { isCapitalStructureValid, projectDraft: project } = useAppSelector(
    (state) => state.project
  );
  const capitalStructures = useAppSelector(selectCapitalStructures);
  const capitalStructureId = Object.keys(capitalStructures)[0];
  const isErfBuildStructure = buildStructureResults?.[capitalStructureId].isErf;
  const {
    details: { currency, denomination },
  } = project;
  const isOpmOnly = project.details.calcMethod === CalcMethod.OPM;

  const persistedSelectedEventIndex = useAppSelector(
    (state) => state.uiValues.userSelections.capitalStructure.capTableErfSelectedEventIndex
  );

  const eventSetId = isOpmOnly
    ? tableResults && Object.keys(tableResults.eventSets).length > 1
      ? EventKey.OpmOnly
      : EventKey.EmptyEventSet
    : project.pwermInput.cases.find((item) => item.caseId === caseId)?.eventSetId ??
      EventKey.EmptyEventSet;
  const caseSelectorShown =
    isErfBuildStructure && project.pwermInput.cases.length > 1 && !isOpmOnly;
  const eventSet = tableResults?.eventSets[eventSetId];

  const eventOptions = useMemo(
    () =>
      eventSet?.events.map((event, index) => {
        const formattedDate = dateFormatter(event.eventDate, {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        });

        const disambiguationLabel = getDisambiguationLabel(event.dateDisambiguation);
        return {
          value: index,
          viewValue: `${formattedDate}${disambiguationLabel ? ` [${disambiguationLabel}]` : ''} (${
            event.eventNarrative
          })`,
        };
      }),
    [eventSet]
  );

  const defaultEventKey = eventOptions[0]?.value ? Number(eventOptions[0]?.value) : 0;

  const resolvedDefaultEventKey = persistedSelectedEventIndex ?? defaultEventKey;

  const formMethods = useForm<{ selectedEventKey: number }>({
    ...formConfigBase,
    defaultValues: {
      selectedEventKey: resolvedDefaultEventKey!,
    },
  });

  const { reset } = formMethods;
  const { selectedEventKey } = formMethods.watch();
  const isSelectedEventKeyValid = eventOptions.some(
    (option) => String(option.value) === String(selectedEventKey)
  );
  useEffect(() => {
    setSecondaryStickyElementHeight(
      '--capital-structure-header-height',
      '--capital-structure-header-height-additional-control',
      eventDateDdRef
    );
  }, [tableResults?.eventSets, eventOptions, setSecondaryStickyElementHeight]);

  useEffect(() => {
    if (!isSelectedEventKeyValid) {
      reset({ selectedEventKey: defaultEventKey });
    }
  }, [reset, isSelectedEventKeyValid, defaultEventKey]);

  useEffect(() => {
    const persistedKey = persistedSelectedEventIndex ?? '';
    if (isSelectedEventKeyValid && selectedEventKey !== persistedKey) {
      dispatch(setCapTableErfSelectedEventIndex(selectedEventKey));
    }
  }, [dispatch, isSelectedEventKeyValid, persistedSelectedEventIndex, selectedEventKey]);

  const selectedEvent = isSelectedEventKeyValid
    ? eventSet?.events[selectedEventKey]
    : eventSet?.events[0];

  const instruments = Object.entries(selectedEvent?.instruments ?? {}).map(([key, value]) => ({
    ...value,
    instrumentId: key,
  }));

  const sortedInstruments = useInstrumentSort(instruments) || [];

  if (!isCapitalStructureValid) {
    const newPath = caseId
      ? generatePath(`../../${RouteConstants.BuildStructure}`)
      : generatePath(`../${RouteConstants.BuildStructure}`);
    return <Navigate to={newPath} />;
  }

  const consistsShareHolderLoanNotes = getEquityInstrumentCountByType(
    instruments,
    InstrumentType.ShareholderLoanNotes
  );
  const consistsPreferenceShares = getEquityInstrumentCountByType(
    instruments,
    InstrumentType.PreferredShares
  );
  const consistsOrdinaryEquities = getEquityInstrumentCountByType(
    instruments,
    InstrumentType.OrdinaryEquity
  );

  const totalInvestment = selectedEvent?.summary.totalInvestment;
  const instrumentTypes = selectedEvent?.summary.instrumentTypes;
  const carryingValueClarificationKey = getCarryingValueClarificationKey(
    Boolean(consistsPreferenceShares),
    Boolean(consistsShareHolderLoanNotes)
  );

  const countColSpan = () => {
    return (
      (consistsOrdinaryEquities ? 1 : 0) +
      (consistsPreferenceShares ? 1 : 0) +
      (consistsShareHolderLoanNotes ? 1 : 0)
    );
  };

  return (
    <div className={styles[PARENT_CLASSNAME]}>
      <div
        className={classNames('main-container-padding-remove', {
          [styles[`${PARENT_CLASSNAME}__container`]]: caseSelectorShown,
        })}>
        <FormProvider {...formMethods}>
          <form>
            {eventOptions.length > 1 && (
              <div ref={eventDateDdRef} className={styles[`${PARENT_CLASSNAME}__event-date`]}>
                <span className={styles[`${PARENT_CLASSNAME}__event-date-label`]}>
                  {l('_EventDate')}
                </span>
                <div className={styles[`${PARENT_CLASSNAME}__event-date-selector`]}>
                  <FormSelect
                    name="selectedEventKey"
                    options={eventOptions}
                    ariaLabel={l('_SelectorAriaLabel', { label: l('_EventDateTitle') })}
                    isGapless
                  />
                </div>
              </div>
            )}
            <table
              className={`table-primary table-primary--zebra table-primary--framed-bottom table-primary--framed table-primary--sticky-first-column ${
                styles[`${PARENT_CLASSNAME}__sticky-first-col`]
              }`}>
              <colgroup>
                <col className={styles[`${PARENT_CLASSNAME}__labels-col`]} />
                <col className={styles[`${PARENT_CLASSNAME}__summary-col`]} />
                {Boolean(consistsShareHolderLoanNotes) && (
                  <col className={styles[`${PARENT_CLASSNAME}__summary-col`]} />
                )}
                {Boolean(consistsPreferenceShares) && (
                  <col className={styles[`${PARENT_CLASSNAME}__summary-col`]} />
                )}
                {Boolean(consistsOrdinaryEquities) && (
                  <col className={styles[`${PARENT_CLASSNAME}__summary-col`]} />
                )}
                {(Object.keys(OwnerType) as Array<keyof typeof OwnerType>).map((key) => {
                  return <col key={`cap-table-col-${key}`} />;
                })}
              </colgroup>
              <thead className="table-primary__sticky-section table-primary__sticky-section--capital-structure-dynamic-header">
                <tr className="table-primary__row--secondary">
                  <th className="table-primary__cell--header-secondary table-primary__cell--vertical-separator table-primary__cell--section-end-separator table-primary__cell--center table-primary__cell--align-bottom">
                    <strong>{l('_Owner')}</strong>
                  </th>
                  <th
                    className="table-primary__cell--header-secondary table-primary__cell--center table-primary__cell--section-end-separator table-primary__cell--align-bottom"
                    colSpan={1 + countColSpan()}>
                    <strong>
                      {l('_Summary')} ({currencyDenominationPair(currency, denomination)})
                    </strong>
                  </th>
                  {sortedInstruments.map((field, index) => (
                    <Fragment key={field.narrative + index + 'headers'}>
                      <th
                        className={classNames(
                          'table-primary__cell--header-secondary table-primary__cell--vertical-separator-start table-primary__cell--section-end-separator table-primary__cell--center table-primary__cell--align-bottom',
                          {
                            [styles[`${PARENT_CLASSNAME}__instrument-col`]]: true,
                          }
                        )}
                        colSpan={2}>
                        <strong>{field.narrative}</strong>
                      </th>
                    </Fragment>
                  ))}
                </tr>
                <tr className="table-primary__row--secondary">
                  <th className="table-primary__cell--header-secondary table-primary__cell--vertical-separator" />
                  <th className="table-primary__cell--header-secondary table-primary__cell--right table-primary__cell--nowrap">
                    <strong>{l('_Investment')}</strong>
                  </th>
                  {Boolean(consistsShareHolderLoanNotes) && (
                    <th className="table-primary__cell--header-secondary table-primary__cell--right table-primary__cell--nowrap">
                      <strong>{l('_SHLs')}</strong>
                    </th>
                  )}
                  {Boolean(consistsPreferenceShares) && (
                    <th className="table-primary__cell--header-secondary table-primary__cell--right table-primary__cell--nowrap">
                      <strong>{l('_PrefShares')}</strong>
                    </th>
                  )}
                  {Boolean(consistsOrdinaryEquities) && (
                    <th className="table-primary__cell--header-secondary table-primary__cell--right table-primary__cell--nowrap">
                      <strong>{l('_OrdinaryEquityTitle')}</strong>
                    </th>
                  )}
                  {sortedInstruments.map((field, index) => (
                    <Fragment key={field.narrative + index + 'headers'}>
                      <th className="table-primary__cell--header-secondary table-primary__cell--right table-primary__cell--vertical-separator-start table-primary__cell--nowrap">
                        <CellValue
                          strong
                          alignment={Alignment.Right}
                          value={getCountWithDenomination(denomination)}
                        />
                      </th>
                      <th className="table-primary__cell--header-secondary table-primary__cell--right table-primary__cell--nowrap">
                        <CellValue
                          strong
                          alignment={Alignment.Right}
                          value={currencyDenominationPair(currency, denomination)}
                        />
                      </th>
                    </Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                <>
                  {(Object.keys(OwnerType) as Array<keyof typeof OwnerType>).map((key) => {
                    const owner = key as keyof typeof OwnerType;
                    const ownerResults = selectedEvent.summary.totalInvestment.owners[owner];
                    const ownerTotal = selectedEvent.summary.totalInvestment.owners[owner];
                    return (
                      ownerResults !== undefined && (
                        <Fragment key={`cap-item-${key}`}>
                          <tr className="table-primary__row--secondary">
                            <th className="table-primary__cell--vertical-separator">
                              <strong>{OwnerType[key]}</strong>
                            </th>
                            <td>
                              <CellValue {...amountRules} value={ownerTotal.value} />
                            </td>
                            {Boolean(consistsShareHolderLoanNotes) && (
                              <td>
                                <CellValue
                                  {...amountRules}
                                  value={instrumentTypes.ShareholderLoanNotes.owners[owner]?.value}
                                />
                              </td>
                            )}
                            {Boolean(consistsPreferenceShares) && (
                              <td>
                                <CellValue
                                  {...amountRules}
                                  value={instrumentTypes.PreferredShares.owners[owner]?.value}
                                />
                              </td>
                            )}
                            {Boolean(consistsOrdinaryEquities) && (
                              <td>
                                <CellValue
                                  {...amountRules}
                                  value={instrumentTypes.OrdinaryEquity.owners[owner]?.value}
                                />
                              </td>
                            )}
                            {sortedInstruments.map((field, index) => {
                              const instrumentOwnerTotals =
                                selectedEvent.instruments[field.instrumentId].owners[owner];
                              return (
                                <Fragment key={field.narrative + index + OwnerType[key]}>
                                  <td className="table-primary__cell--vertical-separator-start">
                                    <CellValue
                                      {...decimalCalculatedViewRules}
                                      value={instrumentOwnerTotals?.shares}
                                    />
                                  </td>
                                  <td>
                                    <CellValue
                                      {...amountRules}
                                      value={instrumentOwnerTotals?.value}
                                    />
                                  </td>
                                </Fragment>
                              );
                            })}
                          </tr>
                          <tr className="table-primary__row--plain">
                            <th className="table-primary__cell--vertical-separator">
                              {l('_PcShareholdingTitle')}
                            </th>
                            <td>
                              <CellValue
                                {...enteredPercentageViewRules}
                                value={getCalculatedProjectValueInPercent(
                                  totalInvestment.owners[owner].valuePercentage
                                )}
                              />
                            </td>
                            {Boolean(consistsShareHolderLoanNotes) && (
                              <td>
                                <CellValue
                                  {...enteredPercentageViewRules}
                                  value={getCalculatedProjectValueInPercent(
                                    instrumentTypes.ShareholderLoanNotes.owners[owner]
                                      ?.valuePercentage
                                  )}
                                />
                              </td>
                            )}
                            {Boolean(consistsPreferenceShares) && (
                              <td>
                                <CellValue
                                  {...enteredPercentageViewRules}
                                  value={getCalculatedProjectValueInPercent(
                                    instrumentTypes.PreferredShares.owners[owner]?.valuePercentage
                                  )}
                                />
                              </td>
                            )}
                            {Boolean(consistsOrdinaryEquities) && (
                              <td>
                                <CellValue
                                  {...enteredPercentageViewRules}
                                  value={getCalculatedProjectValueInPercent(
                                    instrumentTypes.OrdinaryEquity.owners[owner]?.valuePercentage
                                  )}
                                />
                              </td>
                            )}
                            {sortedInstruments.map((field, index) => {
                              const instrumentOwnerTotals =
                                selectedEvent.instruments[field.instrumentId].owners[owner];
                              return (
                                <Fragment key={field.instrumentId + index + OwnerType[key]}>
                                  <td className="table-primary__cell--vertical-separator-start">
                                    <CellValue
                                      {...enteredPercentageViewRules}
                                      value={getCalculatedProjectValueInPercent(
                                        instrumentOwnerTotals?.sharePercentage
                                      )}
                                    />
                                  </td>
                                  <td>
                                    <CellValue
                                      {...enteredPercentageViewRules}
                                      value={getCalculatedProjectValueInPercent(
                                        instrumentOwnerTotals?.valuePercentage
                                      )}
                                    />
                                  </td>
                                </Fragment>
                              );
                            })}
                          </tr>
                          <tr className="table-primary__row--plain">
                            <td className="table-primary__cell--vertical-separator  table-primary__cell--section-separator" />
                            <td
                              className="table-primary__cell--section-separator"
                              colSpan={1 + countColSpan()}
                            />
                            {sortedInstruments.map((field, index) => (
                              <td
                                key={'spacing-row' + index}
                                colSpan={2}
                                className="table-primary__cell--empty-spacer table-primary__cell--vertical-separator-start  table-primary__cell--section-separator"
                              />
                            ))}
                          </tr>
                        </Fragment>
                      )
                    );
                  })}
                </>
                <tr>
                  <th className="table-primary__cell--vertical-separator table-primary__cell--highlight table-primary__cell--section-start-separator">
                    <strong>{l('_Total')}</strong>
                  </th>
                  <td className="table-primary__cell--highlight table-primary__cell--strong table-primary__cell--section-start-separator">
                    <CellValue {...amountRules} value={totalInvestment.total.value} />
                  </td>
                  {Boolean(consistsShareHolderLoanNotes) && (
                    <td className="table-primary__cell--highlight table-primary__cell--strong table-primary__cell--section-start-separator">
                      <CellValue
                        {...amountRules}
                        value={instrumentTypes.ShareholderLoanNotes.total.value}
                      />
                    </td>
                  )}
                  {Boolean(consistsPreferenceShares) && (
                    <td className="table-primary__cell--highlight table-primary__cell--strong table-primary__cell--section-start-separator">
                      <CellValue
                        {...amountRules}
                        value={instrumentTypes.PreferredShares.total.value}
                      />
                    </td>
                  )}
                  {Boolean(consistsOrdinaryEquities) && (
                    <td className="table-primary__cell--highlight table-primary__cell--strong table-primary__cell--section-start-separator">
                      <CellValue
                        {...amountRules}
                        value={instrumentTypes.OrdinaryEquity.total.value}
                      />
                    </td>
                  )}
                  {sortedInstruments.map((field, index) => {
                    const instrumentTotals = selectedEvent.instruments[field.instrumentId].total;
                    return (
                      <Fragment key={field.narrative + index + 'numOfShares'}>
                        <td className="table-primary__cell--highlight table-primary__cell--strong table-primary__cell--section-start-separator table-primary__cell--vertical-separator-start">
                          <CellValue
                            {...decimalCalculatedViewRules}
                            value={instrumentTotals.shares}
                          />
                        </td>
                        <td className="table-primary__cell--highlight table-primary__cell--strong table-primary__cell--section-start-separator">
                          <CellValue {...amountRules} value={instrumentTotals.value} />
                        </td>
                      </Fragment>
                    );
                  })}
                </tr>
                <tr>
                  <th className="table-primary__cell--vertical-separator" />
                  <td />
                  {Boolean(consistsShareHolderLoanNotes) && (
                    <td className="table-primary__cell--strong">
                      <CellValue
                        {...enteredPercentageViewRules}
                        value={getCalculatedProjectValueInPercent(
                          instrumentTypes.ShareholderLoanNotes.total.valuePercentage
                        )}
                      />
                    </td>
                  )}
                  {Boolean(consistsPreferenceShares) && (
                    <td className="table-primary__cell--strong">
                      <CellValue
                        {...enteredPercentageViewRules}
                        value={getCalculatedProjectValueInPercent(
                          instrumentTypes.PreferredShares.total.valuePercentage
                        )}
                      />
                    </td>
                  )}
                  {Boolean(consistsOrdinaryEquities) && (
                    <td className="table-primary__cell--strong">
                      <CellValue
                        {...enteredPercentageViewRules}
                        value={getCalculatedProjectValueInPercent(
                          instrumentTypes.OrdinaryEquity.total.valuePercentage
                        )}
                      />
                    </td>
                  )}
                  {sortedInstruments.map((field, index) => {
                    const instrumentTotals = selectedEvent.instruments[field.instrumentId].total;
                    return (
                      <Fragment key={field.narrative + index + InstrumentType[field.type]}>
                        <td className="table-primary__cell--strong table-primary__cell--vertical-separator-start">
                          <CellValue
                            {...enteredPercentageViewRules}
                            value={getCalculatedProjectValueInPercent(
                              instrumentTotals.sharePercentage
                            )}
                          />
                        </td>
                        <td className="table-primary__cell--strong table-primary--section-end-separator">
                          <CellValue
                            {...enteredPercentageViewRules}
                            value={getCalculatedProjectValueInPercent(
                              instrumentTotals.valuePercentage
                            )}
                          />
                        </td>
                      </Fragment>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </form>
        </FormProvider>
        {carryingValueClarificationKey && (
          <>
            <br />
            <p className={styles[`${PARENT_CLASSNAME}__italic-text`]}>
              {l(carryingValueClarificationKey)}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default CapTableErf;
